import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BizButton } from '../../../Core/Components/Mini/BizButton';
import { BizSelect } from '../../../Core/Components/Mini/BizSelect';
import {
	useCreateMonthReports,
	useGetLeaveSettings,
	useGetNormalizedHours,
	useGetUserInfo,
} from '../../../Core/Hooks/useTimereport';
import { TimereportMonthStats } from '../../../Core/Types/admin/timereport';
import './MonthStatsReport.scss';

type ReportMode = 'time' | 'money';

type ReportModeOption = {
	id: ReportMode;
	name: string;
};

const getHoursByType = (stats: TimereportMonthStats[], systemName: string) =>
	stats.find((s) => s.typeSystemName === systemName)?.hours ?? 0;

type MonthStatsReportProps = {
	stats: TimereportMonthStats[];
	savedHoursBefore: number;
	upToDate: string;
	onClose?: () => void;
	onSave?: (mode: ReportMode) => void;
};

const MonthStatsReport = ({ stats, savedHoursBefore, upToDate, onClose, onSave }: MonthStatsReportProps) => {
	const { t } = useTranslation();
	const mr = (key: string, defaultValue: string) => t(`TIMEREPORT_USER.MONTH_REPORT.${key}`, { defaultValue });

	const dateFrom = stats.length > 0 ? stats[0].dateFrom.split('T')[0] : '';
	const dateTo = stats.length > 0 ? stats[0].dateTo.split('T')[0] : '';
	const { normalizedHours } = useGetNormalizedHours(dateFrom, dateTo);
	const { userInfo } = useGetUserInfo();
	const { leaveSettings } = useGetLeaveSettings();

	const normalTime = getHoursByType(stats, 'NORMALTIME');
	const absence = getHoursByType(stats, 'ABSENCE');
	const parentalLeave = getHoursByType(stats, 'PARENTALLEAVE');
	const vacation = getHoursByType(stats, 'VACATION');
	const overtimeSimple = getHoursByType(stats, 'OVERTIME');
	const overtimeQualified = getHoursByType(stats, 'OVERTIMEQUALIFIED');
	const totalHours = normalTime + absence + parentalLeave + vacation;
	const difference = normalizedHours > 0 ? totalHours - normalizedHours : 0;
	const available = difference;

	const savedHoursPrevious = savedHoursBefore;
	const [selectedMode, setSelectedMode] = useState<ReportMode>('time');
	const { createMonthReports } = useCreateMonthReports();

	const reportModeOptions: ReportModeOption[] = [
		{ id: 'time', name: mr('MODE_TIME', 'Time') },
		{ id: 'money', name: mr('MODE_MONEY', 'Money') },
	];

	const savedHoursFromNormal = selectedMode === 'time' ? available : 0;
	const savedHoursCurrent = savedHoursPrevious + savedHoursFromNormal;
	const savedHoursIsBelowLimit = userInfo != null && savedHoursCurrent < userInfo.savedHoursLimitLower;
	const savedHoursIsAboveLimit = userInfo != null && savedHoursCurrent > userInfo.savedHoursLimitUpper;
	const savedHoursLimitClassName = savedHoursIsBelowLimit
		? ' biz-timereport-month-stats__limit-row--below'
		: savedHoursIsAboveLimit
			? ' biz-timereport-month-stats__limit-row--above'
			: '';

	const moneyFromNormal = selectedMode === 'money' && available > 0 ? available : 0;

	const handleSave = () => {
		createMonthReports({
			upToDate,
			mode: selectedMode,
		});
		onSave?.(selectedMode);
	};

	const period = stats.length > 0 ? `${dateFrom} - ${dateTo}` : '';

	const fmt = (h: number) => {
		const fixed = h.toFixed(2);
		return (fixed.endsWith('.00') ? h.toFixed(0) : fixed).replace('.', ',');
	};

	return (
		<div className='biz-timereport-month-stats'>
			<p className='biz-timereport-month-stats__period'>
				<span className='biz-timereport-month-stats__period-full'>
					{mr('PERIOD_TITLE', 'Month report for the period')} {period}
				</span>
				<span className='biz-timereport-month-stats__period-short'>
					{mr('PERIOD_TITLE_SHORT', 'Month report')} {period}
				</span>
			</p>

			<div className='biz-timereport-month-stats__columns'>
				<div className='biz-timereport-month-stats__left'>
					<h4>{mr('NORMAL_TIME', 'Normal time')}</h4>
					<table className='biz-timereport-month-stats__table'>
						<tbody>
							<tr>
								<td>{mr('NORMAL_TIME', 'Normal time')}:</td>
								<td>{fmt(normalTime)} h</td>
							</tr>
							<tr>
								<td>{mr('ABSENCE', 'Absence')}:</td>
								<td>{fmt(absence)} h</td>
							</tr>
							{(leaveSettings?.parentalLeave.activityId ?? 0) > 0 && (
								<tr>
									<td>{mr('PARENTAL_LEAVE', 'Parental leave')}:</td>
									<td>{fmt(parentalLeave)} h</td>
								</tr>
							)}
							<tr>
								<td>{mr('VACATION', 'Vacation')}:</td>
								<td>{fmt(vacation)} h</td>
							</tr>
						</tbody>
						<tfoot>
							<tr>
								<td>
									<strong>{mr('TOTAL', 'Total')}:</strong>
								</td>
								<td>
									<strong>{fmt(totalHours)} h</strong>
								</td>
							</tr>
							{normalizedHours > 0 && (
								<>
									<tr>
										<td>{mr('NORMALIZED_TIME', 'Normalized time')}:</td>
										<td>{fmt(normalizedHours)} h</td>
									</tr>
									<tr>
										<td>{mr('DIFFERENCE', 'Difference')}:</td>
										<td>{fmt(difference)} h</td>
									</tr>
									<tr>
										<td>
											<strong>{mr('AVAILABLE', 'Available')}:</strong>
										</td>
										<td>
											<strong>{fmt(available)} h</strong>
										</td>
									</tr>
								</>
							)}
						</tfoot>
					</table>

					{normalizedHours > 0 && (
						<div className='biz-timereport-month-stats__mode'>
							<label>{mr('TAKE_OUT_AS', 'Take out as')}:</label>
							<BizSelect
								options={reportModeOptions}
								selectedId={selectedMode}
								onChange={(opt) => setSelectedMode((opt?.id as ReportMode) ?? 'time')}
								getDisabled={(opt) => opt.id === 'money' && available <= 0}
							/>
						</div>
					)}

					{overtimeSimple > 0 && (
						<>
							<h4>{mr('OVERTIME_SIMPLE', 'Simple overtime')}</h4>
							<table className='biz-timereport-month-stats__table'>
								<tbody>
									<tr>
										<td>{mr('TOTAL', 'Total')}:</td>
										<td>{fmt(overtimeSimple)} h</td>
									</tr>
								</tbody>
							</table>
						</>
					)}

					{overtimeQualified > 0 && (
						<>
							<h4>{mr('OVERTIME_QUALIFIED', 'Qualified overtime')}</h4>
							<table className='biz-timereport-month-stats__table'>
								<tbody>
									<tr>
										<td>{mr('TOTAL', 'Total')}:</td>
										<td>{fmt(overtimeQualified)} h</td>
									</tr>
								</tbody>
							</table>
						</>
					)}
				</div>

				{(leaveSettings?.savedHours.activityId ?? 0) > 0 && (
					<div className='biz-timereport-month-stats__right'>
						<h4>{mr('SAVED_HOURS', 'Saved hours')}</h4>
						<table className='biz-timereport-month-stats__table'>
							<tbody>
								<tr>
									<td>{mr('PREVIOUS_VALUE', 'Previous value')}:</td>
									<td>{fmt(savedHoursPrevious)} h</td>
								</tr>
								<tr>
									<td>{mr('FROM_NORMAL_TIME', 'From normal time')}:</td>
									<td>{fmt(savedHoursFromNormal)} h</td>
								</tr>
								<tr>
									<td>{mr('FROM_OVERTIME_SIMPLE', 'From simple overtime')}:</td>
									<td>{fmt(0)} h</td>
								</tr>
								<tr className={`biz-timereport-month-stats__limit-row${savedHoursLimitClassName}`}>
									<td>
										<strong>{mr('CURRENT_VALUE', 'Current value')}:</strong>
									</td>
									<td>
										<strong>{fmt(savedHoursCurrent)} h</strong>
									</td>
								</tr>
							</tbody>
						</table>

						{selectedMode === 'money' && (
							<>
								<h4>{mr('WITHDRAW_MONEY', 'Withdraw money')}</h4>
								<table className='biz-timereport-month-stats__table'>
									<tbody>
										<tr>
											<td>{mr('FROM_NORMAL_TIME', 'From normal time')}:</td>
											<td>{fmt(moneyFromNormal)} h</td>
										</tr>
										<tr>
											<td>{mr('FROM_OVERTIME_SIMPLE', 'From simple overtime')}:</td>
											<td>{fmt(0)} h</td>
										</tr>
										<tr>
											<td>{mr('FROM_OVERTIME_QUALIFIED', 'From qualified overtime')}:</td>
											<td>{fmt(0)} h</td>
										</tr>
									</tbody>
								</table>
							</>
						)}
					</div>
				)}
			</div>

			<div className='biz-timereport-month-stats__actions dialog-btn-actions'>
				{onClose && <BizButton text={t('GLOBAL.CLOSE', 'Close')} onClick={onClose} legacy={false} />}
				<BizButton text={t('GLOBAL.SAVE', 'Save')} onClick={handleSave} isLead={true} legacy={false} />
			</div>
		</div>
	);
};

export default MonthStatsReport;
